import React, { useEffect, useState } from 'react';
import { Check, Copy, Link2, ShieldOff, X } from 'lucide-react';
import driveApi, { errorMessage, ROLES } from '../../api/drive';
import { useDrive } from '../../contexts/DriveProvider';
import { formatDate } from '../../lib/files';

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const parseEmails = (text) => text.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 px-4 py-2">
    <span>{label}</span>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[hsl(var(--primary))]" />
  </label>
);

const CopyButton = ({ url, label = 'Copy link' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch { /* clipboard blocked */ }
      }}
      className="flex h-10 shrink-0 items-center gap-2 rounded-[10px] border border-border bg-card px-3 font-semibold hover:bg-muted"
    >
      {copied ? <Check className="h-4 w-4 text-external" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : label}
    </button>
  );
};

const NewLinkForm = ({ item, onCreated, prefill = '' }) => {
  const { toast } = useDrive();
  const [mode, setMode] = useState('invited');
  const [emails, setEmails] = useState(prefill);
  const [expires, setExpires] = useState(inDays(30));
  const [allowDownload, setAllowDownload] = useState(true);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await driveApi.createLink(item.id, {
        mode,
        emails: mode === 'invited' ? parseEmails(emails) : [],
        expires_at: expires || null,
        allow_download: allowDownload,
        notify_on_download: notify,
      });
      toast(mode === 'invited' ? 'Guest link created and invitations sent' : 'Guest link created');
      onCreated();
    } catch (err) {
      toast(errorMessage(err, 'Could not create the link.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <label className="flex flex-col gap-1.5">
        <span className="text-xs text-muted-foreground">Who can open it</span>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-10 rounded-lg border border-border bg-card px-2">
          <option value="invited">Specific people: secure link + one-time code by email</option>
          <option value="anyone">Anyone with the link (public)</option>
        </select>
      </label>
      {mode === 'invited' && (
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">External emails</span>
          <textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            rows={2}
            placeholder="anna@atelier.example, bob@supplier.example"
            className="rounded-lg border border-border bg-card px-3 py-2 outline-none focus:border-primary"
          />
        </label>
      )}
      <div className="grid grid-cols-1 gap-0 overflow-hidden rounded-xl border border-border sm:grid-cols-2">
        <Toggle label="Allow download" checked={allowDownload} onChange={setAllowDownload} />
        <Toggle label="Email me on download" checked={notify} onChange={setNotify} />
        <label className="flex flex-col gap-1.5 border-t border-border px-4 py-3 sm:col-span-2">
          <span className="text-xs text-muted-foreground">Expires</span>
          <input type="date" value={expires} min={inDays(1)} onChange={(e) => setExpires(e.target.value)} className="h-10 rounded-lg border border-border bg-card px-2" />
        </label>
      </div>
      <button
        type="button"
        onClick={create}
        disabled={busy || (mode === 'invited' && !parseEmails(emails).length)}
        className="h-10 rounded-lg bg-external font-semibold text-white transition hover:opacity-90 disabled:opacity-50 dark:text-black"
      >
        {mode === 'invited' ? 'Create link & send invitations' : 'Create guest link'}
      </button>
    </div>
  );
};

const ExistingLink = ({ link, onChanged }) => {
  const { toast } = useDrive();
  const [adding, setAdding] = useState('');

  const update = async (body, ok) => {
    try {
      await driveApi.updateLink(link.id, body);
      if (ok) toast(ok);
      onChanged();
    } catch (err) {
      toast(errorMessage(err, 'Could not update the link.'), 'error');
    }
  };

  const revoke = async () => {
    try {
      await driveApi.revokeLink(link.id);
      toast('Guest link turned off');
      onChanged();
    } catch (err) {
      toast(errorMessage(err, 'Could not turn off the link.'), 'error');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 p-4">
        <span className="min-w-0 flex-1 truncate tabular text-xs text-muted-foreground" title={link.url}>{link.url}</span>
        <CopyButton url={link.url} />
      </div>
      {link.file_url && link.is_live && (
        <div className="flex flex-col gap-1.5 border-t border-border px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate tabular text-xs text-muted-foreground" title={link.file_url}>{link.file_url}</span>
            <CopyButton url={link.file_url} label="Copy file link" />
          </div>
          <p className="text-xs text-muted-foreground">
            The file itself, without the Drive page around it. Paste it on another site, like hr.lamptwist.be, to play the video or show the image there.
          </p>
        </div>
      )}
      {link.mode === 'invited' && (
        <div className="flex flex-col gap-1 border-t border-border px-4 py-3">
          <span className="text-[13px] font-semibold text-muted-foreground">Guests</span>
          {link.invites.map((inv) => (
            <div key={inv.id} className="flex items-center gap-3 rounded-xl bg-external-soft/60 px-3 py-2">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-2">
                  <b className="truncate">{inv.email}</b>
                  <span className="rounded-full bg-external-soft px-2 py-0.5 text-[11px] font-bold text-external">GUEST</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {inv.last_opened_at ? `Verified, last opened ${formatDate(inv.last_opened_at).replace(/^Today/, 'today')}` : 'Invited, not opened yet'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => update({ remove_emails: [inv.email] }, `${inv.email} removed`)}
                aria-label={`Remove ${inv.email}`}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-card hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <form
            className="mt-1 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const list = parseEmails(adding);
              if (!list.length) return;
              update({ emails: list }, 'Invitation sent');
              setAdding('');
            }}
          >
            <label className="flex-1">
              <span className="sr-only">Invite another email</span>
              <input
                type="text"
                value={adding}
                onChange={(e) => setAdding(e.target.value)}
                placeholder="Invite another email"
                className="h-10 w-full rounded-lg border border-border bg-card px-3 outline-none focus:border-primary"
              />
            </label>
            <button type="submit" className="h-10 rounded-lg border border-border px-3 font-semibold hover:bg-muted">Invite</button>
          </form>
        </div>
      )}
      <div className="grid grid-cols-1 border-t border-border sm:grid-cols-2">
        <Toggle label="Allow download" checked={link.allow_download} onChange={(v) => update({ allow_download: v })} />
        <Toggle label="Email me on download" checked={link.notify_on_download} onChange={(v) => update({ notify_on_download: v })} />
        <label className="flex flex-col gap-1.5 border-t border-border px-4 py-3">
          <span className="text-xs text-muted-foreground">Expires</span>
          <input
            type="date"
            min={inDays(1)}
            value={link.expires_at ? link.expires_at.slice(0, 10) : ''}
            onChange={(e) => update({ expires_at: e.target.value || null }, 'Expiry updated')}
            className="h-10 rounded-lg border border-border bg-card px-2"
          />
        </label>
        <div className="flex items-end border-t border-border px-4 py-3">
          <button type="button" onClick={revoke} className="h-10 w-full rounded-lg border border-destructive/40 font-semibold text-destructive hover:bg-destructive/10">
            Turn off link
          </button>
        </div>
      </div>
    </div>
  );
};

/** Guest-link block shown in the Share dialog for a folder or file. */
const GuestLinks = ({ item, role, prefill = '' }) => {
  const [state, setState] = useState(null);
  const { bump } = useDrive();

  const load = () => driveApi.nodeLinks(item.id).then(setState).catch(() => setState({ blocked: false, links: [] }));
  useEffect(() => { load(); }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = () => { load(); bump(); };
  const canShare = role >= ROLES.EDITOR;

  return (
    <section aria-label="Guest link" className="mx-6 mb-6 overflow-hidden rounded-2xl border border-border">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
        <Link2 className="h-5 w-5 text-external" aria-hidden="true" />
        <div className="flex flex-1 flex-col">
          <b>Share outside LampTwist</b>
          <span className="text-xs text-muted-foreground">Guests don’t need an account. Restricted subfolders stay hidden.</span>
        </div>
      </div>
      {!state && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
      {state?.blocked && (
        <div className="flex gap-2.5 p-4 text-sm">
          <ShieldOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          External sharing is blocked here by folder security.
        </div>
      )}
      {state && !state.blocked && state.links.map((link) => (
        <ExistingLink key={link.id} link={link} onChanged={changed} />
      ))}
      {state && !state.blocked && state.links.length === 0 && (
        canShare
          ? <NewLinkForm key={prefill} item={item} onCreated={changed} prefill={prefill} />
          : <div className="p-4 text-sm text-muted-foreground">Only editors can share this outside LampTwist.</div>
      )}
    </section>
  );
};

export default GuestLinks;
